import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check, Sparkles, Loader2 } from "lucide-react";
import { API } from "@/config";
import BookingModal from "@/components/site/BookingModal";
import ShareButtons from "@/components/site/ShareButtons";

const media = (url) => (url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }) : "");

export default function Post() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState("");
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError("No encontramos este artículo. Puede que ya no esté publicado."));
  }, [slug]);

  return (
    <div data-testid="post-page" className="min-h-screen bg-cream text-obsidian">
      <header className="border-b border-sand">
        <div className="px-4 sm:px-8 lg:px-12 max-w-3xl mx-auto h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" data-testid="post-home-link" className="flex items-baseline gap-1.5">
            <span className="font-display text-2xl font-semibold tracking-tight text-obsidian">
              ISI<span className="text-gold">'S</span>
            </span>
            <span className="text-xs tracking-[0.35em] uppercase text-taupe">Nails</span>
          </Link>
          <button
            onClick={() => setBooking(true)}
            data-testid="post-book-btn"
            className="inline-flex items-center gap-2 rounded-full bg-gold hover:bg-gold-dark text-white text-sm font-medium px-5 py-2.5 transition-colors"
          >
            <Sparkles className="w-4 h-4" /> Reservar cita
          </button>
        </div>
      </header>

      <main className="px-4 sm:px-8 lg:px-12 max-w-3xl mx-auto py-12 sm:py-16">
        <Link to="/#novedades" data-testid="post-back-link" className="inline-flex items-center gap-1.5 text-sm text-taupe hover:text-gold transition-colors">
          <ArrowLeft className="w-4 h-4" /> Todas las novedades
        </Link>

        {error ? (
          <p data-testid="post-error" className="mt-10 rounded-2xl bg-creamcard border border-sand p-8 text-center text-cocoa">{error}</p>
        ) : !post ? (
          <div className="mt-10 flex justify-center text-taupe"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <article className="mt-8">
            <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">{fmt(post.published_at)}</p>
            <h1 data-testid="post-title" className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-3">
              {post.title}
            </h1>
            <p className="text-base text-cocoa mt-5">{post.excerpt}</p>

            {post.image_url && (
              <img src={media(post.image_url)} alt={post.title} data-testid="post-image" className="w-full rounded-2xl border border-sand mt-8 object-cover max-h-[520px]" />
            )}

            <div data-testid="post-body" className="mt-8 grid gap-5">
              {post.body.split(/\n{2,}/).filter(Boolean).map((para, i) => (
                <p key={i} className="text-base text-cocoa leading-relaxed">{para}</p>
              ))}
            </div>

            {post.tips?.length > 0 && (
              <div data-testid="post-tips" className="mt-10 rounded-2xl bg-creamcard border border-sand p-6 sm:p-8">
                <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Mis consejos</p>
                <ul className="mt-4 grid gap-3">
                  {post.tips.map((t, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-cocoa">
                      <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-8">
                {post.tags.map((t) => (
                  <span key={t} className="rounded-full bg-rosesoft text-cocoa text-xs px-3 py-1.5">{t}</span>
                ))}
              </div>
            )}

            <div className="mt-10 border-t border-sand pt-8">
              <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Compartir</p>
              <div className="mt-4">
                <ShareButtons post={post} />
              </div>
            </div>

            <div className="mt-12 rounded-2xl bg-obsidian text-cream p-8 sm:p-10">
              <h2 className="font-display text-2xl">¿Te apetece probarlo?</h2>
              <p className="text-sm text-cream/75 mt-2">Reserva tu cita y lo diseñamos juntas en el estudio.</p>
              <button
                onClick={() => setBooking(true)}
                data-testid="post-cta-book-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold hover:bg-gold-dark text-white text-sm font-medium px-7 py-3.5 transition-colors"
              >
                <Sparkles className="w-4 h-4" /> Reservar mi cita
              </button>
            </div>
          </article>
        )}
      </main>

      <BookingModal open={booking} initialService={null} onClose={() => setBooking(false)} />
    </div>
  );
}
